/* eslint-disable @typescript-eslint/no-explicit-any */
import api from '@/api';
import { PageResult } from '@/core/request/PageResult';
import { API_BASE_URL } from '..';
import {
  AccountReceivableBySaleRequest,
  AccountReceivableCreate,
  AccountReceivableCreateResponse,
  AccountReceivableDetail,
  AccountReceivableEdit,
  AccountReceivableFinancialPending,
  AccountReceivableFinancialSummary,
  AccountReceivableGenerateFinancial,
  AccountReceivableGenerateGroupingRequest,
  AccountReceivableInstallmentCancel,
  AccountReceivableListRequest,
  AccountReceivableListResponse,
  AccountReceivableRangeDateResponse,
  AccountReceivableReverseInstallmentRequest,
  AccountReceivableReverseMultipleInstallmentsRequest,
  AccountReceivableDeleteMultipleInstallmentsRequest,
  AccountReceivableSettleMultipleInstallmentRequest,
  AccountRecivableRenegotiateInstallmentRequest,
  AccountRecivableRenegotiateInstallmentResponse,
  AccountRecivablesettlePartiallyInstallment,
  AccountRecivableSettleSingleInstallment,
  AccountRecivableSplitInstallmentRequest,
  ListAccountReceivableBySaleRequest,
  ListAccountReceivableForGroupingRequest,
  ListAccountReceivableForGroupingResponse,
  AccountReceivableListGroupingResponse,
  AccountReceivableArchiveCreateRequest,
  AccountReceivableArchiveRemoveRequest,
  AccountReceivableFinancialBySaleRequest,
  AccountReceivableFinancialBySaleResponse,
  AccountReceivableIncludeTagRequest,
  AccountReceivableExist,
} from './AccountReceivable.types';
import {
  AccountPayableArchive,
  AccountPayableRangeDateRequest,
} from '../AccountPayable/AccountPayable.types';

const baseUrl = `${API_BASE_URL}/AccountReceivable`;

const accountReceivableServices = {
  async list(params?: AccountReceivableListRequest) {
    const result = (await api.get(baseUrl, params)) as unknown;

    return result as AccountReceivableListResponse;
  },

  async detail(id?: number) {
    const result = (await api.get(`${baseUrl}/${id}`, null)) as unknown;

    return result as AccountReceivableDetail;
  },

  async create(data: AccountReceivableCreate) {
    const result = (await api.post(baseUrl, data)) as unknown;

    return result as AccountReceivableCreateResponse;
  },

  async edit({
    installmentId,
    accountReceivableId,
    ...data
  }: AccountReceivableEdit) {
    const result = (await api.put(
      `${baseUrl}/${accountReceivableId}/installmentReceivableId/${installmentId}`,
      data,
      null,
    )) as unknown;

    return result as void;
  },

  async settleSingleInstallment(data: AccountRecivableSettleSingleInstallment) {
    const result = (await api.put(
      `${baseUrl}/SettleSingleInstallment`,
      data,
      null,
    )) as unknown;

    return result as void;
  },

  async settlePartiallyInstallment(
    data: AccountRecivablesettlePartiallyInstallment,
  ) {
    const result = (await api.put(
      `${baseUrl}/PartiallySettleInstallment`,
      data,
      null,
    )) as unknown;

    return result as void;
  },

  async installmentCancel(data: AccountReceivableInstallmentCancel) {
    const result = (await api.patch(
      `${baseUrl}/CancelInstallment`,
      data,
      null,
    )) as unknown;

    return result as void;
  },

  async settleMultipleInstallment(
    data: AccountReceivableSettleMultipleInstallmentRequest,
  ) {
    const result = (await api.put(
      `${baseUrl}/SettleMultipleInstallment`,
      data,
      null,
    )) as unknown;

    return result as void;
  },

  async reverseInstallment(data: AccountReceivableReverseInstallmentRequest) {
    const result = (await api.put(
      `${baseUrl}/ReverseInstallment`,
      data,
      null,
    )) as unknown;

    return result as void;
  },

  async splitInstallment(data: AccountRecivableSplitInstallmentRequest) {
    const result = (await api.put(
      `${baseUrl}/SplitInstallment`,
      data,
      null,
    )) as unknown;

    return result as void;
  },

  async renegotiateInstallment(
    data: AccountRecivableRenegotiateInstallmentRequest,
  ): Promise<AccountRecivableRenegotiateInstallmentResponse> {
    const result = (await api.post(`${baseUrl}/Renegotiate`, data)) as unknown;

    return result as AccountRecivableRenegotiateInstallmentResponse;
  },

  async listAccountReceivableForGrouping(
    data: ListAccountReceivableForGroupingRequest,
  ) {
    const result = (await api.get(
      `${baseUrl}/ListAccountReceivableForGrouping`,
      data,
    )) as unknown;

    return result as PageResult<ListAccountReceivableForGroupingResponse>;
  },

  async accountReceivableGenerateGrouping(
    data: AccountReceivableGenerateGroupingRequest,
  ) {
    const result = (await api.post(
      `${baseUrl}/GenerateGrouping`,
      data,
    )) as unknown;

    return result as ListAccountReceivableForGroupingResponse;
  },

  async listAccountReceivableBySale(data: ListAccountReceivableBySaleRequest) {
    const result = (await api.get(
      `${baseUrl}/direct-sale/installments`,
      data,
    )) as unknown;

    return result as AccountReceivableListResponse;
  },

  async accountReceivableGenerateBySale(data: AccountReceivableBySaleRequest) {
    const result = (await api.post(
      `${baseUrl}/direct-sale/generate-financial`,
      data,
    )) as unknown;

    return result;
  },

  async accountReceivableFinancialPendingBySale(
    params: AccountReceivableFinancialBySaleRequest,
  ) {
    const result = (await api.get(
      `${baseUrl}/direct-sale/financial-pending`,
      params,
    )) as unknown;

    return result as AccountReceivableFinancialBySaleResponse;
  },

  async accountReceivableFinancialSaleExist(saleId?: number) {
    const result = (await api.get(
      `${baseUrl}/direct-sale/${saleId}/exists`,
      null,
    )) as unknown;

    return result as AccountReceivableExist;
  },

  async accountReceivableFinancialPending(workOrderId?: number) {
    const result = (await api.get(`${baseUrl}/work-order/financial-pending`, {
      workOrderId,
    })) as unknown;

    return result as AccountReceivableFinancialPending;
  },

  async accountReceivableFinancialSummary(workOrderId?: number) {
    const result = (await api.get(`${baseUrl}/work-order/financial-summary`, {
      workOrderId,
    })) as unknown;

    return result as AccountReceivableFinancialSummary;
  },

  async accountReceivableGenerateFinancial(
    data: AccountReceivableGenerateFinancial,
  ) {
    const result = (await api.post(
      `${baseUrl}/work-order/generate-financial`,
      data,
    )) as unknown;

    return result as void;
  },

  async accountReceivableFinancialWorkOrderExist(workOrderId?: number) {
    const result = (await api.get(
      `${baseUrl}/work-order/${workOrderId}/exists`,
      null,
    )) as unknown;

    return result as AccountReceivableExist;
  },

  async accountReceivableRangeDate(params?: AccountPayableRangeDateRequest) {
    const result = (await api.get(`${baseUrl}/RangeDate`, params)) as unknown;

    return result as AccountReceivableRangeDateResponse[];
  },

  async reverseMultipleInstallments(
    data: AccountReceivableReverseMultipleInstallmentsRequest,
  ) {
    const result = (await api.put(
      `${baseUrl}/ReverseMultipleInstallment`,
      data,
      null,
    )) as unknown;

    return result;
  },

  async deleteMultipleInstallments(
    data: AccountReceivableDeleteMultipleInstallmentsRequest,
  ) {
    const result = (await api.put(
      `${baseUrl}/CancelInstallments`,
      data,
      null,
    )) as unknown;

    return result;
  },

  async accountReceivableArchiveCreate(
    data: AccountReceivableArchiveCreateRequest,
  ) {
    const formData = new FormData();

    data.files.forEach((file) => {
      formData.append(`files`, file);
    });

    const result = (await api.post(
      `${baseUrl}/${data.accountReceivableId}/Archives`,
      formData,
      null,
      {
        contentType: 'multpart/form-data',
      },
    )) as unknown;

    return result as AccountPayableArchive[];
  },

  async accountReceivableArchiveList(accountReceivableId?: number) {
    const result = (await api.get(
      `${baseUrl}/${accountReceivableId}/Archives`,
      null,
    )) as unknown;

    return result as AccountPayableArchive[];
  },

  async accountReceivableArchiveRemove(
    params: AccountReceivableArchiveRemoveRequest,
  ) {
    const result = (await api.remove(
      `${baseUrl}/${params.accountReceivableId}/Archives/${params.fileId}`,
      {},
      null,
    )) as unknown;

    return result as void;
  },
};

export async function accountReceivableListGroupingById(
  accountReceivableId?: number,
) {
  const result = (await api.get(
    `${baseUrl}/ListGroupings/${accountReceivableId}`,
    null,
  )) as unknown;

  return result as AccountReceivableListGroupingResponse[];
}

export async function accountReceivableIncludeTag({
  accountReceivableId,
  installmentReceivableId,
  ...data
}: AccountReceivableIncludeTagRequest) {
  const result = (await api.patch(
    `${baseUrl}/${accountReceivableId}/installmentReceivableId/${installmentReceivableId}/tag`,
    data,
    null,
  )) as unknown;

  return result;
}

export default accountReceivableServices;
